import { useEffect } from 'react';
import useGetShoppingCartQuery from "../../../../queries/useGetShoppingCartQuery";
import useCartStore from "../../../../store/cartStore";

interface UseShoppingCartResult {
  shoppingCart: ReturnType<typeof useGetShoppingCartQuery>;
  shoppingCartWithSummary: ReturnType<typeof useGetShoppingCartQuery>;
  /**
   * Refetches shopping cart with summary and updates items in the store
   */
  refetchAndUpdateItems: () => Promise<void>;
}

/**
 * Custom hook for managing shopping cart operations
 * @returns Shopping cart queries and utility functions
 */
export default function useShoppingCart(): UseShoppingCartResult {
  const shoppingCart = useGetShoppingCartQuery();
  const shoppingCartWithSummary = useGetShoppingCartQuery(true);

  const setShoppingItems = useCartStore((state) => state.setShoppingItems);

  // Update shopping items in store when cart data changes
  useEffect(() => {
    updateShoppingItems();
  }, [shoppingCart.data]);

  /**
   * Updates the shopping items in the cart store based on current cart data
   */
  const updateShoppingItems = () => {
    const shoppingCardData = shoppingCart.data;
    if (!shoppingCardData) return;
    setShoppingItems(shoppingCardData.shoppingItems);
  };

  /**
   * Refetches shopping cart with summary and updates items in the store
   * @returns The data from the refetch operation
   */
  const refetchAndUpdateItems = async (): Promise<void> => {
    const response = await shoppingCartWithSummary.refetch();
    const data = response.data;

    if (!data) return;

    setShoppingItems(data.shoppingItems);
  };

  return {
    shoppingCart,
    shoppingCartWithSummary,
    refetchAndUpdateItems
  };
}
